import React, { useState } from 'react';
import styled from 'styled-components'
import useAsyncEffect from 'use-async-effect';
import api from '../../api/api';
import moment from 'moment';
import { GetUserResult, SaveUserRequest, TrustedDeviceView } from '../../model/GetUserResult';
import AnimatedSaveButton from '../AnimatedSaveButton';
import UserPermissionRow from './UserPermissionRow';
import { UserPermission } from '../../model/UserPermission';
import { SaveUserResult } from '../../model/GetUsersResult';
import TextInput from '../form/TextInput';
import UserRebateReport from './UserRebateReport';
import ChooseCompanySelect from './ChooseCompanySelect';

const Style = styled.div`

.row {
    margin: 1rem 0;
}
.save-panel{
    margin-top: 1rem;
}
.add-row {
    font-size: 0.7rem;
    display:flex;
    flex-direction: row-reverse;
    margin-top: 5rem;
}
.add-row-button{
    align-self: center;
    margin-left: 1rem;
}
.table{
    margin: 0.5rem 0;
}
.username, .password{
    max-width:600px;
}
.field{
    margin: 1rem 0;

    & > input {
        margin-top: 0.5rem;
    }
}
.devices {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
}
.checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;

    & > input {
        width: auto;
        margin: 0;
    }
}
.narrow {
    width: 1%;
    white-space: nowrap; /* Prevents text from wrapping */
  }

  .narrow button {
    padding: 2px 6px; /* Optional: reduce button size */
  }
.user-fields-row {
    display: flex;
    gap: 2rem;
    align-items: flex-start;
}
.user-fields-left {
    flex: 0 0 50%;
}
.user-fields-right {
    flex: 0 0 50%;
}
`;

// ponytail: good-enough browser/OS summary, full UA is in the cell tooltip
const describeUserAgent = (ua: string) => {
    if (!ua) return '';
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Other';
    const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} / ${os}` : browser;
}

interface IProps {
    
    match?: { params: { id: string, tab:string }};
}


const UserPage:React.FC<IProps> = (props:IProps) => {

    const id = props.match!.params.id;

    const [ rows, setRows ] = useState<UserPermission[]>([]);
    const [ username, setUsername ] = useState<string>('');
    const [ password, setPassword ] = useState<string>('');
    const [ rebate, setRebate ] = useState<string>('');
    const [ passwordResetRequired, setPasswordResetRequired ] = useState<boolean>(false);
    const [ trustedDevices, setTrustedDevices ] = useState<TrustedDeviceView[]>([]);
    const [ created, setCreated ] = useState<boolean>(false);
    const [ currentId, setCurrentId ] = useState<string>(id);
    // the plaintext password captured at the moment of the last successful save -
    // only this is emailable; editing the field clears it so we never send a stale value
    const [ emailablePassword, setEmailablePassword ] = useState<string>('');
    const [ emailStatus, setEmailStatus ] = useState<'idle'|'sending'|'sent'|'error'>('idle');
    const isNew = id === 'new' && !created;

    useAsyncEffect(async (isMounted)=>{
        if(!isNew){
            const result:GetUserResult = await api().getUser(id);
            if(isMounted()){
                setRows(result.permissions)
                setUsername(result.username);
                setRebate(result.rebate != null ? String(result.rebate) : '');
                setPasswordResetRequired(result.passwordResetRequired);
                setTrustedDevices(result.trustedDevices);
            }
        }
    }, []);

    const save = async ()=>{
        const user:SaveUserRequest = {
            username,
            permissions: rows,
            password,
            passwordResetRequired,
            rebate: rebate === '' ? null : parseFloat(rebate)
        }
        const saveUserResult:SaveUserResult = await api().saveUser(currentId, user);
        if(!saveUserResult.success){
            alert(saveUserResult.errorMessage);
            return false;
        }else{
            console.log('saveUserResult', saveUserResult)
            // capture the just-saved password so it can be emailed; stays available until the field is edited again
            if(password){
                setEmailablePassword(password);
                setEmailStatus('idle');
            }
            if(isNew){
              // don't navigate away (it would discard the password) - switch to edit mode for the new id in place
              const routeId = saveUserResult.id.replace(/^users\//, '');
              setCurrentId(routeId);
              setCreated(true);
              window.history.replaceState(null, '', '/' + saveUserResult.id);
            }
            return true;
        }

    }

    const clearTrustedDevices = async ()=>{
        if(!window.confirm('Clear trusted devices? The user\'s next login will not require an emailed code and will re-trust that browser.')) return;
        await api().clearTrustedDevices(currentId);
        setTrustedDevices([]);
    }

    const sendPasswordEmail = async ()=>{
        setEmailStatus('sending');
        const result = await api().emailPassword(currentId, emailablePassword);
        if(result.success){
            setEmailStatus('sent');
        }else{
            setEmailStatus('error');
            alert(result.errorMessage);
        }
    }
    const addCompany = (item: {combinedTicker:string, companyName:string})=>{
        if(rows.filter(x=>x.combinedTicker===item.combinedTicker).length){
            alert('Row with this Company already exists')
        }else{
            setRows([ ...rows, {combinedTicker: item.combinedTicker, companyName:item.companyName, roles: []}])
        }
    }

    const onCheckChanged = (combinedTicker:string, role:string, isChecked:boolean)=>{
        setRows(rows.map(x=>{
            if(x.combinedTicker === combinedTicker){
               const roles = x.roles.filter(x=>x !== role);
               if(isChecked){
                roles.push(role);
               }
               
                return {
                    ...x,
                    roles 
                }
            }else{
                return x;
            }
            
        }))
        return
    }
    const onDelete = (combinedTicker:string)=>{
        setRows(rows.filter(x=>x.combinedTicker !== combinedTicker))
    }

    return <Style>
        <h2>User</h2>

        <div className='save-panel'>
        <AnimatedSaveButton onClick={save} />
        </div>

            <div className='user-fields-row'>
            <div className='user-fields-left'>
            <div className='field'><div className='field-label'>Username: </div>
            <TextInput value={username}
            onChange={(e:React.ChangeEvent<HTMLInputElement>)=>setUsername(e.target.value)}
            disabled={!isNew}
            className="username"
            placeholder="Email address"
            />
            </div>

            <div className='field'><div className='field-label'>Password: </div>
            <TextInput value={password}
            onChange={(e:React.ChangeEvent<HTMLInputElement>)=>{ setPassword(e.target.value); setEmailablePassword(''); setEmailStatus('idle'); }}
            className="password"
            type="password"
            placeholder="Leave blank unless changing"
            />
            </div>

            <div className='field'>
                <label className='checkbox-label'><input type='checkbox' checked={passwordResetRequired}
                onChange={(e:React.ChangeEvent<HTMLInputElement>)=>setPasswordResetRequired(e.target.checked)} />
                Reset Password</label>
            </div>

            { emailablePassword !== '' &&
            <div className='field'>
                <button type='button' onClick={sendPasswordEmail} disabled={emailStatus==='sending' || emailStatus==='sent'}>
                    { emailStatus==='sent' ? 'Sent ✓' : emailStatus==='sending' ? 'Sending…' : 'Email password to user' }
                </button>
            </div>
            }
            </div>

            <div className='user-fields-right'>
            <div className='field'><div className='field-label'>Rebate %: </div>
            <TextInput value={rebate}
            onChange={(e:React.ChangeEvent<HTMLInputElement>)=>setRebate(e.target.value)}
            className="username"
            type="number"
            step="0.01"
            placeholder="Leave blank for no rebate"
            />
            </div>

            {!isNew && <div className='field'><div className='field-label'>Trusted devices: </div>
                {trustedDevices.length > 0 && <table className='table devices'>
                    <thead><tr><th>Browser</th><th>IP</th><th>First seen</th><th>Last used</th></tr></thead>
                    <tbody>{trustedDevices.map((d, i) => <tr key={i}>
                        <td title={d.userAgent}>{describeUserAgent(d.userAgent)}</td>
                        <td>{d.ip}</td>
                        <td>{moment(d.created).format('D MMM YY')}</td>
                        <td>{moment(d.lastUsed).format('D MMM YY HH:mm')}</td>
                    </tr>)}</tbody>
                </table>}
                <button type='button' onClick={clearTrustedDevices} disabled={trustedDevices.length === 0}>Clear trusted devices</button>
            </div>}

            {!isNew && rebate !== '' && <UserRebateReport userId={currentId} />}
            </div>
            </div>

            <ChooseCompanySelect onAdd={addCompany} />
            <table className='table'>
                <thead><tr>
                    <th>Ticker</th>
                    <th>Company Name</th>
                    <th>Permission</th>
                    <th className='narrow'></th>
                    </tr></thead>
                <tbody>

                { rows.map((x,i)=> <tr key={i}><UserPermissionRow permission={x} onCheckChanged={onCheckChanged} onDelete={onDelete} /></tr>)}

                </tbody>
            </table>
    

    </Style>
}

export default UserPage;